/**
 * The original bytes of a file sent for ingest, for the source viewer or a
 * download. Like every other download, these fetch with the auth headers an
 * `<a href>` cannot send.
 */

import { getOntologyServiceBaseUrl } from '../../config'
import { PROXY_API_V1_URL, createOntologyHeaders, saveBlob } from '../core'
import type { FetchSiteStoredDocumentResult } from './site-document'
import { SourceDocUnavailableError } from './source-doc'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

async function fetchInput(url: string): Promise<FetchSiteStoredDocumentResult> {
  const headers = createOntologyHeaders()
  headers.delete('Content-Type')
  const response = await fetch(url, { headers })
  if (response.status === 404) {
    throw new SourceDocUnavailableError('The file is no longer stored')
  }
  if (!response.ok) {
    throw new Error(`file fetch failed (${response.status})`)
  }
  return {
    blob: await response.blob(),
    contentType: response.headers.get('Content-Type')?.split(';')[0]?.trim() || ''
  }
}

/** A file a sender pushed to an intake, whatever became of its run. */
export function fetchIntakeItemFile(itemId: string): Promise<FetchSiteStoredDocumentResult> {
  return fetchInput(
    `${PROXY_API_V1_URL}/ontology/ingest/intake-items/${encodeURIComponent(itemId)}/file`
  )
}

/** A file a tabular run stored when it started. */
export function fetchTabularRunInput(
  jobId: string,
  fileName: string
): Promise<FetchSiteStoredDocumentResult> {
  return fetchInput(
    `${ONTOLOGY_SERVICE_URL}/ingest/jobs/${encodeURIComponent(jobId)}/tabular/inputs/${encodeURIComponent(fileName)}`
  )
}

async function saveInput(
  load: Promise<FetchSiteStoredDocumentResult>,
  fileName: string
): Promise<void> {
  let file: FetchSiteStoredDocumentResult
  try {
    file = await load
  } catch (error) {
    throw new Error(
      error instanceof SourceDocUnavailableError
        ? `${fileName} is no longer stored.`
        : `${fileName} could not be downloaded.`
    )
  }
  saveBlob(file.blob, fileName)
}

export function downloadIntakeItemFile(itemId: string, fileName: string): Promise<void> {
  return saveInput(fetchIntakeItemFile(itemId), fileName)
}

export function downloadTabularRunInput(jobId: string, fileName: string): Promise<void> {
  return saveInput(fetchTabularRunInput(jobId, fileName), fileName)
}
